import React, { useState } from 'react';
import { Sparkles, Loader2, Check, PenLine, X, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { capGuide, capGuideDraft } from '../../lib/capstoneApi';

/**
 * Reusable "✨ Guide suggestion" panel. Collapsed by default; never blocks typing.
 * Props:
 *  - topic: guide topic key
 *  - projectId: existing project id (omit for wizard drafts)
 *  - draftPayload: context object for wizard mode
 *  - onUse(text, origin, suggestion): origin is 'ai' (Use this) or 'ai_edited' (starting point)
 *  - label: optional custom collapsed label
 */
export const GuidePanel = ({ topic, projectId, draftPayload, onUse, label }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [dismissed, setDismissed] = useState([]);
  const [ask, setAsk] = useState('');

  const fetchSuggestions = async (extra = '') => {
    setLoading(true);
    try {
      const res = projectId
        ? await capGuide(projectId, topic, extra)
        : await capGuideDraft({ ...(draftPayload || {}), topic, extra });
      setSuggestions(res.suggestions || []);
      setDismissed([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Guide is unavailable right now');
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && suggestions === null && !loading) fetchSuggestions();
  };

  const visible = (suggestions || []).filter((_, i) => !dismissed.includes(i));

  return (
    <div className="mt-2" data-testid={`guide-panel-${topic}`}>
      <button
        type="button"
        onClick={toggle}
        data-testid={`guide-toggle-${topic}`}
        className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-amber-300/90 hover:text-amber-200 transition-colors"
      >
        <Sparkles className="w-3.5 h-3.5" />
        {label || 'Guide suggestion'}
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {open && (
        <div className="mt-2 rounded-xl border border-amber-300/20 bg-amber-300/[0.04] p-3.5">
          {loading && (
            <div className="flex items-center gap-2 text-xs text-white/50 py-3">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> SAMANVAI Guide is thinking…
            </div>
          )}
          {!loading && visible.length === 0 && suggestions !== null && (
            <div className="text-xs text-white/45 py-2">No suggestions left — ask for a different angle below.</div>
          )}
          {!loading && visible.map((s, idx) => {
            const i = (suggestions || []).indexOf(s);
            return (
              <div key={i} className="mb-3 last:mb-0 rounded-lg bg-white/[0.03] border border-white/8 p-3" data-testid={`guide-suggestion-${topic}-${idx}`}>
                <div className="text-[12px] font-bold text-white/85 mb-1">{s.title}</div>
                {s.text && <div className="text-[12px] text-white/65 leading-relaxed whitespace-pre-wrap mb-1.5">{s.text}</div>}
                {s.fields && (
                  <div className="space-y-1.5 mb-2 border-l-2 border-amber-300/25 pl-2.5">
                    {Object.entries(s.fields).map(([k, v]) => (
                      <div key={k} className="text-[11px] leading-relaxed">
                        <span className="uppercase tracking-wider text-amber-200/60 font-bold text-[9px] mr-1.5">{k.replace(/_/g, ' ')}</span>
                        <span className="text-white/60">{v}</span>
                      </div>
                    ))}
                  </div>
                )}
                {s.rationale && <div className="text-[10.5px] text-amber-200/60 italic mb-2">{s.rationale}</div>}
                <div className="flex flex-wrap gap-2">
                  <button type="button" data-testid={`guide-use-${topic}-${idx}`}
                    onClick={() => { onUse(s.text, 'ai', s); setDismissed((d) => [...d, i]); }}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-amber-300 text-slate-900 text-[11px] font-bold hover:bg-amber-200">
                    <Check className="w-3 h-3" /> Use this
                  </button>
                  <button type="button"
                    onClick={() => { onUse(s.text, 'ai_edited', s); setDismissed((d) => [...d, i]); }}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-amber-300/40 text-amber-200 text-[11px] font-semibold hover:bg-amber-300/10">
                    <PenLine className="w-3 h-3" /> Use as starting point
                  </button>
                  <button type="button"
                    onClick={() => setDismissed((d) => [...d, i])}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-white/45 text-[11px] hover:text-white/70">
                    <X className="w-3 h-3" /> No thanks
                  </button>
                </div>
              </div>
            );
          })}
          {!loading && (
            <div className="flex gap-2 mt-3">
              <input
                value={ask}
                onChange={(e) => setAsk(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && ask.trim()) { fetchSuggestions(ask.trim()); setAsk(''); } }}
                placeholder="Ask Guide something else — e.g. 'a more industry-specific option'"
                data-testid={`guide-ask-${topic}`}
                className="flex-1 bg-white/[0.04] border border-white/12 rounded px-2.5 py-1.5 text-[11.5px] text-white placeholder:text-white/25 focus:outline-none focus:border-amber-300/50"
              />
              <button type="button" disabled={!ask.trim()}
                onClick={() => { fetchSuggestions(ask.trim()); setAsk(''); }}
                className="px-2.5 py-1.5 rounded border border-white/15 text-white/60 hover:text-white disabled:opacity-30">
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <div className="text-[9.5px] text-white/30 mt-2.5 italic">
            AI-generated starting points — not verified facts. You and your mentor own the final content.
          </div>
        </div>
      )}
    </div>
  );
};

export default GuidePanel;
